import {
  Grid,
  GridCell,
  Icon,
  IconButton,
  Image,
  PageSection,
  Select,
  SelectOption,
  sendToast,
  Spacer,
  Stack,
  TextBody,
  TextSubtitle,
} from '@pypestream/design-system';
import { CSSProperties, FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  getMaskingRuleSetSelect,
  type ReportingApiCredentialRow,
  type RuleSet,
} from '../../utils';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';

// The Auto-masking rule set + Live Agent Platforms blocks persist on the
// channel's "Save Changes", each as a MEMBERSHIP change and nothing more:
// masking via `assignPypeRuleGroup`, the platform via `assignPypeConnection`
// (add/remove a `pype_connection_pype` join row). The frontend does NOT write
// `agent_routing` — or any other routing field — onto the channel's pype; the
// backend propagates it to every bound pype (backend#1262). Reintroducing a pype
// write here puts two writers on the same field.
//
// The Reporting API block is a read-only viewer: keys are scoped to the pype, not
// the channel, so there's nothing to assign — the "Manage …" link handles
// credential CRUD in Project Settings.

const PURPLE_LINK_COLOR = 'var(--purple-300, #8573e7)';

/** A project-level Live Agent Platform connector, as a dropdown option. */
export interface PlatformConnectionOption {
  id: string;
  name: string;
  /** The connector's `agent_routing`. Informational — the backend writes it. */
  agentRouting: string;
  /** Pre-resolved platform logo (external connectors resolved via callback URL). */
  logo: string;
}

// The full key is only ever shown once at creation; here we show a masked form.
const maskKey = (key: string): string =>
  key.length > 4 ? `${'•'.repeat(8)}${key.slice(-4)}` : key;

// Read-only grey fields for the reporting Name + Key. Plain divs (not a DS
// Input) so both share one disabled/grey look and the Key can host a clickable
// eye button — the DS Input's suffix slot races with React mounting the button.
const READONLY_FIELD_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minHeight: '38px',
  padding: '0 12px',
  border: '1px solid var(--gray-200, #e1e3e8)',
  borderRadius: '8px',
  background: 'var(--gray-100, #f4f5f7)',
};

const READONLY_VALUE_STYLE: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '14px',
  color: 'var(--gray-400, #838a97)',
};

const ManageLink: FC<{
  label: string;
  /** Project Settings section path segment the link navigates to. */
  section: string;
  testId: string;
}> = ({ label, section, testId }) => {
  const navigate = useNavigate();
  const projectId = useProStudioCtxSelector((ctx) => ctx.project?.id);

  return (
    <Stack
      test-id={testId}
      alignItems="center"
      gutter="2xsmall"
      nowrap
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      onClick={() => projectId && navigate(`/${projectId}/settings/${section}`)}
    >
      <TextBody
        size="small"
        style={{ ['--ps-theme-text-color' as string]: PURPLE_LINK_COLOR }}
      >
        {label}
      </TextBody>
      <Icon
        name="arrow-in-circle"
        size="small"
        style={{ ['--icon-color' as string]: PURPLE_LINK_COLOR }}
      />
    </Stack>
  );
};

/**
 * The three states every one of these blocks has: still loading, failed, or
 * ready. Kept in one place because the blocks used to disagree — the Live Agent
 * Platforms block treated an empty list as "none configured" while its load was
 * still in flight, and said so in a sentence a user could act on wrongly.
 *
 * The note lives in a live region that is ALWAYS mounted (empty when the block
 * is ready), which is what makes the change announceable: a region that appears
 * together with its text is not reliably read out (WCAG 4.1.3).
 */
const BlockLoadState: FC<{
  loaded: boolean;
  failed: boolean;
  /** Names the data in both notes, e.g. "masking rule sets". */
  subject: string;
  loadingTestId: string;
  errorTestId: string;
  children: ReactNode;
}> = ({ loaded, failed, subject, loadingTestId, errorTestId, children }) => (
  <>
    <div role="status">
      {failed ? (
        <TextBody test-id={errorTestId} size="small" variant="secondary">
          {`Couldn't load ${subject}. Reload the page to try again.`}
        </TextBody>
      ) : !loaded ? (
        <TextBody test-id={loadingTestId} size="small" variant="secondary">
          {`Loading ${subject}…`}
        </TextBody>
      ) : null}
    </div>
    {loaded && !failed ? children : null}
  </>
);

export const ChannelSettingsTemplateSections: FC<{
  /** All project rule sets, for the Auto-masking dropdown. */
  ruleSets: RuleSet[];
  /**
   * Whether `ruleSets` has been LOADED. An empty array otherwise means "still
   * loading", and the block would claim the project has no rule sets while its
   * load is still in flight. The Live Agent Platforms block below has the same
   * wrong-empty-state and does NOT have this gate yet — it is loaded LAST in
   * the serialized chain, so it makes the false claim for longest (see the
   * load-chain comment in `channel-settings-form`). Only the NOTE is mirrored
   * here, not a fix over there.
   */
  ruleSetsLoaded: boolean;
  /** Whether that load FAILED — so the block stops promising a spinner. */
  ruleSetsLoadFailed: boolean;
  /** Currently selected rule set id ('' = no explicit binding → project default). */
  selectedRuleSetId: string;
  onRuleSetChange: (ruleSetId: string) => void;
  /** Project-level Live Agent Platform connectors for the channel's env. */
  platformConnections: PlatformConnectionOption[];
  /** Whether the connectors have been LOADED — an empty array is not "none". */
  platformConnectionsLoaded: boolean;
  /** Whether that load FAILED. */
  platformConnectionsLoadFailed: boolean;
  /** The connector currently bound to the channel ('' = none matched). */
  selectedConnectionId: string;
  /** Bind a connector to the channel (parent dispatches `assignPypeConnection`). */
  onSelectConnection: (connectionId: string) => void;
  /** Reporting API credentials on this channel's pype (read-only viewer). */
  credentials: ReportingApiCredentialRow[];
  /** Viewer-role users may read these bindings but not change them. */
  isViewer: boolean;
  /** Tighter sections, for a narrow side panel (the Design stage's). */
  compact?: boolean;
}> = ({
  ruleSets,
  ruleSetsLoaded,
  ruleSetsLoadFailed,
  selectedRuleSetId,
  onRuleSetChange,
  platformConnections,
  platformConnectionsLoaded,
  platformConnectionsLoadFailed,
  selectedConnectionId,
  onSelectConnection,
  credentials,
  isViewer,
  compact = false,
}) => {
  const sectionSize = compact ? 'xsmall' : 'small';
  const titleSize = compact ? 'xsmall' : 'medium';
  const titleGap = compact ? 'small' : 'medium';
  // Reporting API is a read-only display of the credential bound to this
  // channel's pype (the first one, if several exist). The key is masked until
  // the eye is clicked; once revealed, clicking it copies to the clipboard.
  //
  // This is the only place in the app where a Reporting API key can be
  // unmasked — the credentials table renders `maskKey` unconditionally, and
  // the create modal only echoes a key the current user just minted. Viewers
  // therefore get the masked form with no eye and no copy. Note this is a UI
  // gate only: the key is already in the client payload, so withholding it
  // from viewers for real means not fetching it (the shape `get-project-data`
  // uses for provider credentials) or not returning it from the backend.
  const reportingCredential = credentials[0];
  const [keyRevealed, setKeyRevealed] = useState(false);
  const canRevealKey = !isViewer;
  const showFullKey = keyRevealed && canRevealKey;
  const copyKey = (value: string) => {
    navigator.clipboard?.writeText(value);
    sendToast({
      toastId: 'ps-channel-reporting-key-copied',
      type: 'success',
      text: 'Copied to clipboard',
      autoClose: 2000,
    });
  };

  const activeConnection = platformConnections.find(
    (option) => option.id === selectedConnectionId
  );

  // The dropdown's options and the value the channel's current binding maps to.
  // The project default is represented ONCE, by the empty-valued option, and an
  // explicit binding to it normalises onto that option (PSTUDIO-4051).
  const {
    options: ruleSetOptions,
    value: ruleSetValue,
    hasDefault: hasDefaultOption,
  } = getMaskingRuleSetSelect(ruleSets, selectedRuleSetId);

  return (
    <>
      {/* 10px gaps between the template sections (DS default is 24px). */}
      <PageSection
        size={sectionSize}
        test-id="ps-channel-live-agent-platforms-section"
        style={{ marginBottom: '10px' }}
      >
        <TextSubtitle
          size={titleSize}
          test-id="ps-channel-live-agent-platforms-title"
        >
          Live Agent Platforms
        </TextSubtitle>
        <Spacer size={titleGap} />
        <BlockLoadState
          loaded={platformConnectionsLoaded}
          failed={platformConnectionsLoadFailed}
          subject="live agent platforms"
          loadingTestId="ps-channel-live-agent-platforms-loading"
          errorTestId="ps-channel-live-agent-platforms-error"
        >
          {platformConnections.length ? (
            <Select
              test-id="ps-channel-live-agent-platforms-select"
              variant="outlined"
              placeholder="Select a platform"
              disabled={isViewer}
              value={selectedConnectionId}
              onChange={(e) =>
                onSelectConnection((e.target as HTMLSelectElement).value)
              }
            >
              {activeConnection && (
                <Image
                  src={activeConnection.logo}
                  width="24px"
                  height="24px"
                  slot="prefix"
                />
              )}
              <SelectOption value="">None</SelectOption>
              {platformConnections.map((option) => (
                <SelectOption key={option.id} value={option.id}>
                  <Image
                    src={option.logo}
                    width="24px"
                    height="24px"
                    slot="prefix"
                  />
                  {option.name}
                </SelectOption>
              ))}
            </Select>
          ) : (
            <TextBody
              test-id="ps-channel-live-agent-platforms-empty"
              size="small"
              variant="secondary"
            >
              No live agent platforms configured for this environment yet.
            </TextBody>
          )}
        </BlockLoadState>
        <Spacer size="xsmall" />
        <Stack alignItems="center" gutter="2xsmall">
          <TextBody size="small" variant="secondary">
            Connected platform for live agent escalation.
          </TextBody>
          <ManageLink
            label="Manage platforms in Project Settings"
            section="live-agent-platforms"
            testId="ps-channel-live-agent-platforms-manage"
          />
        </Stack>
      </PageSection>

      <PageSection
        size={sectionSize}
        test-id="ps-channel-reporting-api-section"
        style={{ marginBottom: '10px' }}
      >
        <TextSubtitle size={titleSize} test-id="ps-channel-reporting-api-title">
          Reporting API
        </TextSubtitle>
        <Spacer size={titleGap} />
        {reportingCredential ? (
          <Grid>
            <GridCell xsmall="12" small="6">
              <TextBody size="small">Name</TextBody>
              <Spacer size="2xsmall" />
              <div
                data-test-id="ps-channel-reporting-api-name"
                style={READONLY_FIELD_STYLE}
              >
                <span style={READONLY_VALUE_STYLE}>
                  {reportingCredential.name || 'Untitled credential'}
                </span>
              </div>
            </GridCell>
            <GridCell xsmall="12" small="6">
              <TextBody size="small">Key</TextBody>
              <Spacer size="2xsmall" />
              <div
                data-test-id="ps-channel-reporting-api-key"
                style={READONLY_FIELD_STYLE}
              >
                <span
                  onClick={
                    showFullKey
                      ? () => copyKey(reportingCredential.key ?? '')
                      : undefined
                  }
                  title={showFullKey ? 'Click to copy' : undefined}
                  style={{
                    ...READONLY_VALUE_STYLE,
                    cursor: showFullKey ? 'pointer' : 'default',
                  }}
                >
                  {showFullKey
                    ? reportingCredential.key
                    : maskKey(reportingCredential.key ?? '')}
                </span>
                {canRevealKey && (
                  <IconButton
                    test-id="ps-channel-reporting-api-key-toggle"
                    name={keyRevealed ? 'hide' : 'show'}
                    size="small"
                    onClick={() => setKeyRevealed((visible) => !visible)}
                  />
                )}
              </div>
            </GridCell>
          </Grid>
        ) : (
          <TextBody size="small" variant="secondary">
            No Reporting API credentials on this channel&apos;s connection yet.
          </TextBody>
        )}
        <Spacer size="xsmall" />
        <TextBody size="small" variant="secondary">
          Reporting API credential(s) bound to this channel.
        </TextBody>
        <Spacer size="2xsmall" />
        <ManageLink
          label="Manage credentials in Project Settings"
          section="reporting-api-credentials"
          testId="ps-channel-reporting-api-manage"
        />
      </PageSection>

      <PageSection size={sectionSize} test-id="ps-channel-auto-masking-section">
        <TextSubtitle size={titleSize} test-id="ps-channel-auto-masking-title">
          Auto-masking rule set
        </TextSubtitle>
        <Spacer size={titleGap} />
        {/*
          An empty value means "no rule set explicitly assigned", which is not
          the same as "no masking": the channel falls back to the project default
          (PSTUDIO-3804), so that option is named after the default and carries a
          `(Default)` suffix. The default is NOT repeated in the list below — it
          used to be, which showed it twice, and a channel explicitly bound to it
          is normalised onto this same option instead (PSTUDIO-4051).

          With no default in the project there is nothing an empty value could
          mean, so no empty-valued option is rendered at all — the old `None`
          label is gone, and with it the way to leave a channel unmasked, which
          is what the ticket asks for. With no rule sets whatsoever there is
          nothing to pick, so the Select gives way to a note rather than an
          empty dropdown — but only once the load has ANSWERED, or the note
          would state something false while the rule sets are still on their
          way. `BlockLoadState` owns those three states for this block and the
          Live Agent Platforms one alike.
        */}
        <BlockLoadState
          loaded={ruleSetsLoaded}
          failed={ruleSetsLoadFailed}
          subject="masking rule sets"
          loadingTestId="ps-channel-auto-masking-loading"
          errorTestId="ps-channel-auto-masking-error"
        >
          {ruleSetOptions.length ? (
            <Select
              test-id="ps-channel-auto-masking-select"
              variant="outlined"
              // Kept unconditional: the DS Select paints its placeholder only
              // while the display input is empty, and a selected option (the
              // default included) fills it. So it shows while nothing is
              // selected — a project with no default and no explicit binding (the
              // state this ticket leaves without a "None" to pick) — plus a brief
              // first paint before the DS resolves its slotted options. A binding
              // to a DELETED set cannot reach it: `initialRuleSetId` is derived by
              // searching the SAME list that builds these options, so a vanished
              // set reads as '' and lands on the default option instead.
              placeholder="Select a rule set"
              disabled={isViewer}
              value={ruleSetValue}
              onChange={(e) =>
                onRuleSetChange((e.target as HTMLSelectElement).value)
              }
            >
              {ruleSetOptions.map((option) => (
                <SelectOption key={option.value} value={option.value}>
                  {option.label}
                </SelectOption>
              ))}
            </Select>
          ) : (
            <TextBody
              test-id="ps-channel-auto-masking-empty"
              size="small"
              variant="secondary"
            >
              No masking rule sets in this project yet.
            </TextBody>
          )}
        </BlockLoadState>
        <Spacer size="xsmall" />
        <TextBody size="small" variant="secondary">
          Masking ruleset applied to all conversational records and transcripts.
          {hasDefaultOption &&
            ' Channels with no rule set of their own use the default.'}
        </TextBody>
        <Spacer size="2xsmall" />
        <ManageLink
          label="Manage rules in Project Settings"
          section="data-privacy"
          testId="ps-channel-auto-masking-manage"
        />
      </PageSection>
    </>
  );
};
